// One set-up step for one site.

import { useState, type FormEvent } from 'react';
import type { SiteDraft } from '../../../../src/draft.ts';
import { api, message } from '../../api.ts';
import type { SiteView } from '../../types.ts';
import { Loading, Problem, Shell, useLoad, useRefreshWhile } from '../../ui.tsx';
import { MapStep } from './MapStep.tsx';
import { PosterStep } from './PosterStep.tsx';
import { ReviewStep } from './ReviewStep.tsx';
import { SetupShell, STEPS, type StepId } from './SetupShell.tsx';
import { TabletStep } from './TabletStep.tsx';
import { TestStep } from './TestStep.tsx';
import { WordingStep } from './WordingStep.tsx';
import { ZonesStep } from './ZonesStep.tsx';

export function SetupPage({ siteId, step }: { siteId: string; step: string }) {
  const site = useLoad(() => api<SiteView>('GET', `/sites/${siteId}`), [siteId]);
  // While a change waits for the ops tablet, the page updates itself once it's approved there.
  useRefreshWhile(site.data?.changes.some((c) => c.status === 'waiting') ?? false, site.reload);
  const id = (STEPS.some(([s]) => s === step) ? step : 'map') as StepId;
  if (!site.data) {
    return <Shell>{site.error ? <Problem text={site.error} /> : <Loading />}</Shell>;
  }
  return (
    <SetupShell site={site.data} step={id}>
      {id === 'details' && <DetailsStep site={site.data} reload={site.reload} />}
      {id === 'map' && <MapStep site={site.data} reload={site.reload} />}
      {id === 'zones' && <ZonesStep site={site.data} reload={site.reload} />}
      {id === 'wording' && <WordingStep site={site.data} reload={site.reload} />}
      {id === 'tablet' && <TabletStep site={site.data} reload={site.reload} />}
      {id === 'review' && <ReviewStep site={site.data} reload={site.reload} />}
      {id === 'poster' && <PosterStep site={site.data} />}
      {id === 'test' && <TestStep site={site.data} />}
    </SetupShell>
  );
}

function DetailsStep({ site, reload }: { site: SiteView; reload: () => void }) {
  const [clientName, setClient] = useState(site.clientName);
  const [siteName, setSite] = useState(site.siteName);
  const [timeZone, setTz] = useState(site.timeZone);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await api('PUT', `/sites/${site.siteId}/draft`, { clientName, siteName, timeZone, draft: site.draft as SiteDraft });
      setSaved(true);
      reload();
    } catch (err) {
      setError(message(err));
    }
  };
  return (
    <form className="card" style={{ gap: 18, padding: 24 }} onSubmit={save}>
      <div className="form-row">
        <label className="field">
          Client
          <input className="input" value={clientName} onChange={(e) => setClient(e.target.value)} required />
        </label>
        <label className="field">
          Site name
          <input className="input" value={siteName} onChange={(e) => setSite(e.target.value)} required />
        </label>
        <label className="field">
          Site time zone
          <input className="input" value={timeZone} onChange={(e) => setTz(e.target.value)} required />
        </label>
        <label className="field">
          Site ID (in the QR code)
          <input className="input mono" value={site.siteId} disabled />
        </label>
      </div>
      <p className="faint small">Subscription: yearly, starting when the site goes live.{site.live ? ' A new name reaches phones with the next approved change.' : ''}</p>
      {error && <p className="error">{error}</p>}
      {saved && <p className="green small">Saved.</p>}
      <div className="row">
        <button className="btn primary small" type="submit">
          Save
        </button>
      </div>
    </form>
  );
}
